import { useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEOMeta } from '../components/SEOMeta';
import { JsonLd } from '../components/JsonLd';
import {
  articleSchema,
  blogPostBreadcrumbItems,
  breadcrumbSchema,
} from '../lib/jsonld';
import type { Lang } from '../lib/routes';
import { Section, Container, SectionTitle } from '../components/ui';
import { ArticleHeader, ArticleRenderer, BlogCard } from '../components/sections/blog';
import { FinalCTA } from '../components/sections/home';
import type { ContentBlock } from '../components/sections/blog';
import styles from './BlogPost.module.css';

interface Article {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  category: string;
  readingTime: number;
  thumbnail: string;
  body: ContentBlock[];
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation('blog');
  const lang: Lang = i18n.language === 'en' ? 'en' : 'fr';

  const articles = useMemo(() => {
    return t('articles', { returnObjects: true }) as Record<string, Article>;
  }, [t]);

  const article = slug ? articles[slug] : undefined;

  const related = useMemo(() => {
    if (!article) return [];
    return Object.values(articles)
      .filter((a) => a.category === article.category && a.slug !== article.slug)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 3);
  }, [articles, article]);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <>
      <SEOMeta
        title={`${article.title} — QBP.Solutions`}
        description={article.description}
        ogType="article"
        ogImage={article.thumbnail}
      />
      <link
        rel="alternate"
        type="application/rss+xml"
        title={lang === 'en' ? 'QBP Solutions — Blog' : 'QBP Solutions — Blog'}
        href={lang === 'en' ? '/en/rss.xml' : '/rss.xml'}
      />
      <JsonLd
        data={[
          articleSchema(article, lang),
          breadcrumbSchema(blogPostBreadcrumbItems(article.title, article.slug, lang)),
        ]}
      />

      <ArticleHeader
        title={article.title}
        date={article.date}
        author={article.author}
        category={article.category}
        readingTime={article.readingTime}
        thumbnail={article.thumbnail}
      />

      <Section variant="light">
        <Container>
          <ArticleRenderer body={article.body} />
        </Container>
      </Section>

      {related.length > 0 && (
        <Section variant="gray">
          <Container>
            <SectionTitle title={t('article.relatedArticles')} centered />
            <div className={styles.relatedGrid}>
              {related.map((a) => (
                <BlogCard key={a.slug} {...a} />
              ))}
            </div>
          </Container>
        </Section>
      )}

      <FinalCTA />
    </>
  );
}
